import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { Spin } from 'antd'
import requests from '../../../axios/requests'
import useTrajectory from './useTrajectory'
import { pluralN } from './genUtils'
import s from './gen.module.scss'
import c from './courses.module.scss'

const COVERS = ['#1F2A44', '#14352B', '#2B2414', '#2A1F3D', '#1E3340']

// Шаг 3 «Созданные»: курсы, сгенерированные из планов обучения.
const GeneratedCourses = () => {
  const history = useNavigate()
  const { list } = useTrajectory()
  const [plans, setPlans] = useState(null)
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('all')
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    Promise.all([
      requests.users.get_plannings().then((r) => r.data?.results || []),
      requests.quests.get_quest_quest([], 1, 200, 0, '', 'all').then((r) => r.data?.results || []),
    ])
      .then(([pl, qs]) => {
        if (!alive) return
        setPlans(pl)
        setCourses(qs)
      })
      .catch((err) => console.error(err))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const items = useMemo(() => {
    if (!plans) return []
    const byId = {}
    courses.forEach((x) => (byId[x.id] = x))
    const skillName = (id) => list.find((x) => x.id === id)?.name
    return plans
      .filter((pl) => pl.quest?.id)
      .map((pl) => ({ plan: pl, course: byId[pl.quest.id] || null, skill: skillName(pl.speciality_tag) }))
      .sort((a, b) => (b.plan.created_at || '').localeCompare(a.plan.created_at || ''))
  }, [plans, courses, list])

  const pct = (it) => Math.round(it.course?.progress?.progress || 0)
  const counts = {
    all: items.length,
    progress: items.filter((it) => pct(it) > 0 && pct(it) < 100).length,
    done: items.filter((it) => pct(it) >= 100).length,
    fresh: items.filter((it) => pct(it) === 0).length,
  }
  const query = q.trim().toLowerCase()
  const shown = items
    .filter((it) =>
      status === 'all'
        ? true
        : status === 'done'
        ? pct(it) >= 100
        : status === 'progress'
        ? pct(it) > 0 && pct(it) < 100
        : pct(it) === 0
    )
    .filter(
      (it) =>
        !query ||
        (it.course?.name || '').toLowerCase().includes(query) ||
        (it.skill || '').toLowerCase().includes(query)
    )

  const open = (it) => history(`/student/course/lesson/${it.plan.quest.id}`)

  return (
    <section className={s.stage} aria-label="Созданные курсы">
      <div className={s.stagehead}>
        <div>
          <div className={s.kicker}>Шаг 3 из 3</div>
          <h2>Созданные курсы</h2>
          <div className={cn(s.muted, s.small)}>Материалы дописываются в фоне — можно проходить уже готовые модули</div>
        </div>
      </div>

      <div className={c.toolbar}>
        <div className={s.seg} role="group" aria-label="Статус">
          {[
            ['all', 'Все'],
            ['fresh', 'Не начаты'],
            ['progress', 'В процессе'],
            ['done', 'Пройдены'],
          ].map(([key, label]) => (
            <button
              type="button"
              key={key}
              className={cn({ [s.segOn]: status === key })}
              aria-pressed={status === key}
              onClick={() => setStatus(key)}
            >
              {label} · {counts[key]}
            </button>
          ))}
        </div>
        <div className={c.toolbarRight}>
          <label className={s.field} style={{ flex: 1 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input placeholder="Поиск по курсам" aria-label="Поиск по курсам" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <button type="button" className={cn(s.btn, s.pri)} onClick={() => history('/backoffice/skill-set/generate-course')}>
            + Сгенерировать
          </button>
        </div>
      </div>

      {loading ? (
        <Spin style={{ margin: '40px auto' }} />
      ) : !items.length ? (
        <div className={cn(s.card, s.empty)}>
          <h3 style={{ marginBottom: 6 }}>Здесь появятся курсы, созданные ИИ</h3>
          <div style={{ marginBottom: 14 }}>Выберите навык и вариант плана на шаге 2 — курс соберётся за пару минут</div>
          <button type="button" className={cn(s.btn, s.pri)} onClick={() => history('/backoffice/skill-set/generate-course')}>
            Перейти к генерации
          </button>
        </div>
      ) : !shown.length ? (
        <div className={cn(s.card, s.empty)}>Ничего не нашлось</div>
      ) : (
        <div className={c.grid}>
          {shown.map((it, i) => {
            const course = it.course
            const pr = course?.progress || {}
            const done = pct(it) >= 100
            const started = pct(it) > 0
            const img = course?.image || course?.y_image
            return (
              <article key={it.plan.id} className={c.course}>
                <div
                  className={c.cover}
                  style={img ? { backgroundImage: `url(${img})` } : { background: COVERS[i % COVERS.length] }}
                >
                  <span className={cn(c.st, done ? c.stDone : started ? c.stGo : c.stNew)}>
                    {done ? '✓ Пройден' : started ? `В процессе · ${pct(it)}%` : 'Не начат'}
                  </span>
                  <span className={c.kind}>Курс</span>
                </div>
                <div className={c.body}>
                  <div>
                    {it.skill && <div className={cn(s.muted, s.small)}>Навык · {it.skill}</div>}
                    <h3>{course?.name || `Курс по навыку «${it.skill || '—'}»`}</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className={s.small} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Пройдено</span>
                      <b>{started ? `${pct(it)}%` : 'Не начат'}</b>
                    </div>
                    <div className={s.bar}>
                      <i style={{ width: `${pct(it)}%` }} />
                    </div>
                  </div>
                  <div className={c.cnt}>
                    {pr.count_video > 0 && <span>{'\u25B6\uFE0E'} {pluralN(pr.count_video, ['видео', 'видео', 'видео'])}</span>}
                    {pr.count_article > 0 && <span>▤ {pluralN(pr.count_article, ['статья', 'статьи', 'статей'])}</span>}
                    {pr.count_quiz > 0 && <span>✓ {pluralN(pr.count_quiz, ['тест', 'теста', 'тестов'])}</span>}
                    {pr.count_task > 0 && <span>✎ {pluralN(pr.count_task, ['задание', 'задания', 'заданий'])}</span>}
                    {pr.count_scorm_task > 0 && <span>SCORM</span>}
                  </div>
                  <div className={c.actions}>
                    <button type="button" className={cn(s.btn, s.pri)} style={{ flex: 1 }} onClick={() => open(it)}>
                      {done ? 'Открыть курс' : started ? 'Продолжить' : 'Начать'}
                    </button>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default GeneratedCourses
